import React, { useState, useEffect, useMemo } from "react";
import { DocRenderer } from "react-doc-viewer";
import * as XLSX from "xlsx";
import { CircularProgress, Typography } from "@mui/material";
import CustomTable, {
  TableColumn,
} from "@/components/common/table/CustomTable";

interface ExcelRow {
  [key: string]: string | number;
}

const XLSXViewer: DocRenderer = ({ mainState: { currentDocument } }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [excelData, setExcelData] = useState<{
    headers: string[];
    rows: ExcelRow[];
  }>({ headers: [], rows: [] });

  useEffect(() => {
    const fetchAndParseXLSX = async () => {
      if (!currentDocument?.uri) {
        setError("No document provided.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(currentDocument.uri);
        if (!response.ok) throw new Error("Failed to fetch document.");

        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
          type: "array",
        });

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData: string[][] = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
        });

        if (jsonData.length > 0) {
          const headers = jsonData[0].map(String);
          const rows = jsonData.slice(1).map((row) => {
            const rowData: ExcelRow = {};
            headers.forEach((header, index) => {
              rowData[header] = row[index] || "";
            });
            return rowData;
          });

          setExcelData({ headers, rows });
        }
        setError(null);
      } catch (err) {
        setError("Error loading XLSX file.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAndParseXLSX();
  }, [currentDocument?.uri]);

  const columns: TableColumn<ExcelRow>[] = useMemo(() => {
    return excelData.headers.map((header) => ({
      header,
      accessor: header,
      sortable: true,
      sortKey: header,
    }));
  }, [excelData.headers]);

  const fetchTableData = async ({
    page,
    limit,
    search,
    sortColumn,
    sortDirection,
  }: {
    page: number;
    limit: number;
    search: string;
    sortColumn: string;
    sortDirection: "asc" | "desc";
  }) => {
    // Filter data based on search
    let filteredData = [...excelData.rows];
    if (search) {
      filteredData = filteredData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(search.toLowerCase()),
        ),
      );
    }

    // Sort data
    if (sortColumn) {
      filteredData.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];
        const comparison = String(aValue).localeCompare(String(bValue));
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    // Paginate data
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return {
      ok: true,
      data: {
        data: paginatedData,
        meta: {
          count: filteredData.length,
          page,
          limit,
        },
      },
    };
  };

  return (
    <div className="tw-p-4 tw-rounded-lg tw-bg-white tw-shadow-md tw-w-full">
      <Typography variant="h6" className="tw-text-xl tw-font-bold tw-mb-4">
        Excel File Preview
      </Typography>

      {loading && (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-40">
          <CircularProgress />
        </div>
      )}

      {error && (
        <Typography className="tw-text-red-500 tw-font-semibold tw-text-center">
          {error}
        </Typography>
      )}

      {!loading && !error && excelData.rows.length > 0 && (
        <CustomTable<ExcelRow>
          columns={columns}
          fetchData={fetchTableData}
          hideControls={false}
          hidePagination={false}
          removeInitialPadding={true}
          noDataMessage="No data available in the Excel file"
          noSearchDataMessage="No matching records found"
          forceRefresh={false}
          setForceRefresh={() => {}}
          customPaginationDefaultValues={{
            limit: 5,
          }}
        />
      )}

      {!loading && !error && excelData.rows.length === 0 && (
        <Typography className="tw-text-gray-500 tw-text-center">
          No data available.
        </Typography>
      )}
    </div>
  );
};

XLSXViewer.fileTypes = ["xlsx", "xls"];
XLSXViewer.weight = 1;

export default XLSXViewer;
